import {GrowthBook} from '@growthbook/growthbook'
import {GrowthBookProvider} from '@growthbook/growthbook-react'
import type {LinksFunction, LoaderFunctionArgs, MetaFunction} from '@remix-run/node'
import {json} from '@remix-run/node'
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
} from '@remix-run/react'
import {MoonIcon, SunIcon} from '@sanity/icons'
import {Box, Button, type ThemeColorSchemeKey} from '@sanity/ui'
import {withSentry} from '@sentry/remix'
import {useEffect, useMemo, useState} from 'react'
import {z} from 'zod'

import {VanillaErrorMessage} from '~/components/error/VanillaErrorMessage'
import {ThemeProvider, useTheme} from '~/components/ThemeProvider'
import {ThemeToggleProvider} from '~/components/themeToggle'
import * as styles from '~/root.css'
import {createGrowthBook} from '~/services/growthbook.server'
import {windowEnvSchema} from '~/utils'

import {parseColorScheme} from './models/theme-scheme'
import {formatError} from './utils/error'

export const meta: MetaFunction = () => [
  {charset: 'utf-8'},
  {name: 'robots', content: 'noindex'},
  {name: 'viewport', content: 'width=device-width,initial-scale=1'},
  {name: 'theme-color', content: '#ffffff'},
  {title: 'Connect Vercel to Sanity'},
]

export const links: LinksFunction = () => {
  return [
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '96x96',
      href: 'https://www.sanity.io/static/images/favicons/favicon-96x96.png',
    },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '32x32',
      href: 'https://www.sanity.io/static/images/favicons/favicon-32x32.png',
    },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '16x16',
      href: 'https://www.sanity.io/static/images/favicons/favicon-16x16.png',
    },
  ]
}

const loaderSchema = z.object({
  themeScheme: z.enum(['dark', 'light']).nullish(),
  ENV: windowEnvSchema,
  growthbookFeatures: z.string().optional(),
})

export async function loader({request, context}: LoaderFunctionArgs) {
  let growthbookFeatures: string | undefined
  try {
    const gb = await createGrowthBook()
    growthbookFeatures = JSON.stringify(gb.getFeatures())
    gb.destroy()
  } catch {
    // Fallback to empty if GrowthBook fails
  }
  return json(
    loaderSchema.parse({
      themeScheme: await parseColorScheme!(request),
      ENV: {
        SENTRY_ENVIRONMENT: context.SENTRY_ENVIRONMENT,
        SENTRY_TRACES_SAMPLE_RATE: context.SENTRY_TRACES_SAMPLE_RATE,
        RELEASE_HASH: context.RELEASE_HASH,
      },
      growthbookFeatures,
    }),
  )
}

function parseGrowthbookFeatures(raw?: string) {
  if (!raw) return {}
  try {
    return JSON.parse(raw)
  } catch {
    return {}
  }
}

function App() {
  const data = useLoaderData<typeof loader>()
  const [activeScheme, setActiveScheme] = useState(data.themeScheme || undefined)
  const [scheme, themeClass] = useTheme(activeScheme)
  const growthbook = useMemo(
    () =>
      new GrowthBook({
        features: parseGrowthbookFeatures(data.growthbookFeatures),
      }),
    [data.growthbookFeatures],
  )

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal
    const formData = new FormData()
    formData.append('colorScheme', scheme)
    fetch('/create/api/theme-scheme', {signal, method: 'POST', body: formData}).catch((err) => {
      if (err.name === 'AbortError') return
      // Note: this request fails often, see https://sanity.sentry.io/issues/6385875362/events/ee018a19b4ef490283ab3738a75700b0/
      // but given that it's non-critical and only about color scheme the error reporting has been disabled for now
      // captureException(err)
      // eslint-disable-next-line no-console
      console.error(err)
    })
    return () => {
      controller.abort()
    }
  }, [scheme])

  const isNewInstallUi = growthbook.getFeatureValue('new-install-ui', false)

  return (
    <html lang="en" className={`${styles.root} ${themeClass}`}>
      <head>
        <Meta />
        <Links />
        {typeof document === 'undefined' ? '__STYLES__' : null}
      </head>
      <body className={styles.root}>
        <GrowthBookProvider growthbook={growthbook}>
          <ThemeToggleProvider scheme={scheme} setScheme={setActiveScheme}>
            <ThemeProvider scheme={scheme}>
              <Outlet />
              {!isNewInstallUi ? (
                <Box margin={[2, 3, 4]} className={styles.schemeToggle}>
                  <Button
                    aria-label="Toggle color scheme"
                    icon={scheme === 'light' ? SunIcon : MoonIcon}
                    mode="bleed"
                    onClick={() => {
                      setActiveScheme((nextScheme: ThemeColorSchemeKey | undefined) =>
                        nextScheme === 'light' ? 'dark' : 'light',
                      )
                    }}
                  />
                </Box>
              ) : null}
            </ThemeProvider>
          </ThemeToggleProvider>
        </GrowthBookProvider>
        <ScrollRestoration />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(data.ENV)}`,
          }}
        />
        <Scripts />
      </body>
    </html>
  )
}

export default withSentry(App)

export function ErrorBoundary() {
  const [, themeClass] = useTheme()
  const error = useRouteError()

  if (isRouteErrorResponse(error)) {
    const title = `${error.status} ${error.statusText}`
    return (
      <html lang="en" className={`${styles.root} ${themeClass}`}>
        <head>
          <title>{title}</title>
          <Meta />
          <Links />
        </head>
        <body className={styles.root}>
          <VanillaErrorMessage title={title} details={JSON.stringify(error.data, null, 2)} />
          <Scripts />
        </body>
      </html>
    )
  }

  // eslint-disable-next-line no-console
  console.error(error)
  const _error = formatError(error)
  return (
    <html lang="en" className={`${styles.root} ${themeClass}`}>
      <head>
        <title>Uncaught error!</title>
        <Meta />
        <Links />
      </head>
      <body className={styles.root}>
        <VanillaErrorMessage title={`Uncaught error: ${_error?.message}`} details={_error?.stack} />
        <Scripts />
      </body>
    </html>
  )
}
